<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { MessageItem } from "../../../types/localMessage";
import { getRandomBg, getRandomColor } from "../../../utils/randomBg";
import LeaderCrown from "./LeaderCrown.vue";
import { uploadState } from "../../../states/chatState";
import { isAdmin } from "../../../states/adminsState";

const props = defineProps<{
  message: MessageItem;
}>();

const getAvatarText = (name: string) => {
  let res = "";
  if (name) {
    const nf = name.split(" ");
    res += nf[0][0].toUpperCase();
    if (nf[1] && nf[1].length > 0) {
      res += nf[1][0].toUpperCase();
    }
  }
  return res;
};

const localMessageId = computed(() => {
  return props.message.local?.id;
});
const flash = ref(false);
watch(
  () => [uploadState.percent, uploadState.messageId],
  (next, prev) => {
    if (next[0] === 0 && prev[0] === 100 && prev[1] === localMessageId.value) {
      flash.value = true;
      setTimeout(() => {
        flash.value = false;
      }, 2000);
    }
  },
);
</script>
<template>
  <div class="base-message-container">
    <div v-if="localMessageId && localMessageId === uploadState.messageId" class="loader" :style="{
      right: `${100 - uploadState.percent}%`,
    }"></div>
    <div class="load-success" v-else-if="flash"></div>
    <div class="avatar" :style="{
      backgroundColor: getRandomBg(
        getAvatarText(message.author.displayedName),
      ),
    }">
      <img v-if="message.author.avatar" :src="message.author.avatar" alt="" />
      <span v-else class="avatar-text">{{
        getAvatarText(message.author.displayedName)
        }}</span>
    </div>
    <div class="right">
      <div class="message-author">
        <span class="leader" v-if="isAdmin(message.author.id)">
          <LeaderCrown />
        </span>
        <span class="name" :style="{
          color: getRandomColor(getAvatarText(message.author.displayedName))
        }">{{ message.author.displayedName }}</span>
      </div>
      <div class="content">
        <slot />
      </div>
    </div>
  </div>
</template>
<style scoped>
.base-message-container {
  position: relative;
  width: 100%;
  padding: 0 20px 0 16px;
  display: flex;
  gap: 8px;
}

.avatar {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-top: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar img {
  width: 32px;
  height: 32px;
}

.avatar .avatar-text {
  width: 100%;
  text-align: center;
  color: #ffffff;
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.right {
  display: flex;
  flex-direction: column;
  width: 100px;
  flex-grow: 1;
  gap: 4px;
}

.message-author {
  height: 20px;
  width: 100%;
  display: flex;
  gap: 4px;
  align-items: center;
}

.message-author .leader {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.message-author .name {
  font-family: Open Sans;
  font-size: 14px;
  font-weight: 600;
  line-height: 19.07px;
  text-align: left;
  color: rgba(216, 216, 216, 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loader {
  position: absolute;
  z-index: -1;
  left: 0;
  top: -4px;
  bottom: -6px;
  background-color: rgba(73, 73, 73, 0.27);
  transition: all 10s cubic-bezier(0, 0.78, 0, 0.99);
}

.load-success {
  position: absolute;
  z-index: -1;
  left: 0;
  right: 0;
  top: -4px;
  bottom: -6px;
  opacity: 0;
  background-color: rgba(73, 73, 73, 0.27);
  animation: blinker 2s ease-out;
}

@keyframes blinker {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>
